import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  colorLandingBrand,
  colorLandingBrandSoft,
  colorLandingBrandStrong,
  colorLandingEventSoft,
  colorLandingEventText,
  colorLandingInk,
  colorLandingLine,
  colorLandingMuted,
  colorLandingSurface,
  radiusLandingControl,
} from "src/design/tokens";

type OfferKind = "event" | "offer";

const KIND_STYLES: Record<OfferKind, { background: string; color: string }> = {
  event: { background: colorLandingEventSoft, color: colorLandingEventText },
  offer: { background: colorLandingBrandSoft, color: colorLandingBrandStrong },
};

const ITEMS: {
  kind: OfferKind;
  icon: string;
  nameKey: string;
  tagKey: string;
}[] = [
  {
    kind: "event",
    icon: "🎉",
    nameKey: "landingPage.ecosystem.dashboard.liveMusic",
    tagKey: "landingPage.ecosystem.dashboard.liveMusicTag",
  },
  {
    kind: "offer",
    icon: "🏷️",
    nameKey: "landingPage.ecosystem.dashboard.happyHour",
    tagKey: "landingPage.ecosystem.dashboard.happyHourTag",
  },
];

/** "Offers & Events" card: what the restaurant pushes into the consumer app. */
export const OffersEventsCard = () => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        mt: 1.5,
        border: `1px solid ${colorLandingLine}`,
        borderRadius: radiusLandingControl,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          px: 1.75,
          py: 1.5,
          borderBottom: `1px solid ${colorLandingLine}`,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: colorLandingInk }}>
            {t("landingPage.ecosystem.dashboard.offersEvents")}
          </Typography>
          <Typography sx={{ fontSize: 10, color: colorLandingMuted, mt: 0.25 }}>
            {t("landingPage.ecosystem.dashboard.offersEventsSub")}
          </Typography>
        </Box>
        <Typography
          sx={{ fontSize: 11, fontWeight: 700, color: colorLandingBrandStrong }}
        >
          {t("landingPage.ecosystem.dashboard.create")}
        </Typography>
      </Box>

      <Box sx={{ p: 1.25, display: "flex", flexDirection: "column", gap: 1.125 }}>
        {ITEMS.map((item) => (
          <Box
            key={item.nameKey}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.375,
              border: `1px solid ${colorLandingLine}`,
              borderRadius: radiusLandingControl,
              px: 1.5,
              py: 1.25,
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "9px",
                display: "grid",
                placeItems: "center",
                fontSize: 15,
                flexShrink: 0,
                backgroundColor: KIND_STYLES[item.kind].background,
              }}
            >
              {item.icon}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{ fontSize: 13, fontWeight: 700, color: colorLandingInk }}
              >
                {t(item.nameKey)}
              </Typography>
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  mt: 0.375,
                  fontSize: 9,
                  fontWeight: 700,
                  px: 1,
                  py: 0.25,
                  borderRadius: 999,
                  backgroundColor: KIND_STYLES[item.kind].background,
                  color: KIND_STYLES[item.kind].color,
                }}
              >
                {t(item.tagKey)}
              </Box>
            </Box>
            <Box
              sx={{
                position: "relative",
                width: 34,
                height: 20,
                borderRadius: 999,
                flexShrink: 0,
                backgroundColor: colorLandingBrand,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: 2,
                  right: 2,
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  backgroundColor: colorLandingSurface,
                },
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};
